import { FC, memo, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import gsap from 'gsap'

import css from './PageNavigation.module.scss'

import { Route } from '@/data/routes'

import { MOTION } from '@/utils/motion-values'

import useLayout from '@/hooks/use-layout'
import { useRefs } from '@/hooks/use-refs'

import CtaLink from '@/components/CtaLink/CtaLink'
import Heading from '@/components/Heading/Heading'
import Label from '@/components/Label/Label'
import Paragraph from '@/components/Paragraph/Paragraph'

import LeftArrow from '@/svgs/left-arrow.svg'
import RightArrow from '@/svgs/right-arrow.svg'

export interface PageNavigationProps {
  className?: string
  prev: Route
  next: Route
}

export type ViewRefs = {
  root: HTMLDivElement
  wrapper: HTMLDivElement
  contentWrapper: HTMLDivElement
  //
  leftLabel: HTMLDivElement
  leftProjectTitle: HTMLDivElement
  leftArrowSvg: HTMLDivElement
  leftBigTitle: HTMLDivElement
  //
  rightLabel: HTMLDivElement
  rightProjectTitle: HTMLDivElement
  rightArrowSvg: HTMLDivElement
  rightBigTitle: HTMLDivElement
}

export interface ViewProps extends PageNavigationProps {}

// View (pure and testable component, receives props from the controller)
// export const View = forwardRef<HTMLDivElement, ViewProps>(({ className, prev, next }, ref) => {

export const View: FC<ViewProps> = ({ className, prev, next }) => {
  const viewRefs = useRefs<ViewRefs>()
  const layout = useLayout()
  const timelineRef = useRef<gsap.core.Timeline | null>(null)
  const [onReady, setReady] = useState(false)

  useEffect(() => {
    //  gsap.set(viewRefs.wrapper.current, { opacity: 0 })
    gsap.set(viewRefs.contentWrapper.current, { opacity: 0 })

    const readyTimeout = setTimeout(() => {
      setReady(true)
    }, MOTION.general.delayForLayout)

    return () => {
      if (readyTimeout) clearTimeout(readyTimeout)
    }
  }, [viewRefs])

  useEffect(() => {
    if (!onReady) return

    timelineRef.current = gsap
      .timeline({
        delay: 0.5,
        scrollTrigger: {
          trigger: viewRefs.root.current,
          start: 'top 100%'
        }
      })
      // .set(viewRefs.wrapper.current, { opacity: 1 }, 0)
      .set(viewRefs.contentWrapper.current, { opacity: 1 }, 0)
      .fromTo(
        [viewRefs.leftArrowSvg.current, viewRefs.rightArrowSvg.current],
        { opacity: 0 },
        { opacity: 1, duration: 0.8, ease: 'ease-opacity' },
        0.5
      )
      .textRiseByCharsIn(
        viewRefs.leftBigTitle.current!,
        { duration: 1.5, charDuration: 1.5, charOffset: 0.075, immediateRender: true, ease: 'ease-split-text' },
        0.5
      )
      .textRiseByCharsIn(
        viewRefs.rightBigTitle.current!,
        { duration: 1.5, charDuration: 1.5, charOffset: 0.075, immediateRender: true, ease: 'ease-split-text' },
        0.5
      )

    // On desktop animate in the extra labels and move the container up a bit
    if (layout.desktop) {
      timelineRef.current
        .from(viewRefs.wrapper.current, { y: 60, duration: 1, ease: 'ease-transform' }, 0) //
        .from(
          [
            viewRefs.leftLabel.current,
            viewRefs.leftProjectTitle.current,
            viewRefs.rightLabel.current,
            viewRefs.rightProjectTitle.current
          ],
          {
            opacity: 0,
            duration: 0.8,
            ease: 'ease-opacity'
          },
          0.4
        )
    }

    return () => {
      if (timelineRef.current) timelineRef.current.kill()
    }
  }, [layout.desktop, viewRefs, onReady])

  return (
    <div className={classNames('PageNavigation', css.root, className)} ref={viewRefs.root}>
      <div className={css.wrapper} ref={viewRefs.wrapper}>
        <div className={css.contentWrapper} ref={viewRefs.contentWrapper}>
          <div className={css.left}>
            <div className={css.description}>
              <Paragraph paragraphType={2} italic ref={viewRefs.leftLabel}>
                Previous
              </Paragraph>
              <Label labelType={3} uppercase ref={viewRefs.leftProjectTitle}>
                {prev.title}
              </Label>
            </div>

            <CtaLink className={css.link} href={prev.path}>
              <div ref={viewRefs.leftArrowSvg}>
                <LeftArrow className={css.svg} />
              </div>
              <Heading as="h3" italic ref={viewRefs.leftBigTitle}>
                Previous
              </Heading>
            </CtaLink>
          </div>
          <div className={css.right}>
            <div className={css.description}>
              <Paragraph paragraphType={2} italic ref={viewRefs.rightLabel}>
                Next
              </Paragraph>
              <Label labelType={3} uppercase ref={viewRefs.rightProjectTitle}>
                {next.title}
              </Label>
            </div>
            <CtaLink className={css.link} href={next.path}>
              <Heading as="h3" italic ref={viewRefs.rightBigTitle}>
                Next
              </Heading>
              <div ref={viewRefs.rightArrowSvg}>
                <RightArrow className={css.svg} />
              </div>
            </CtaLink>
          </div>
        </div>
      </div>
    </div>
  )
}

View.displayName = 'PageNavigation-View'

// Controller (handles global state, router, data fetching, etc. Feeds props to the view component)
const PageNavigation: FC<PageNavigationProps> = (props) => {
  return <View {...props} />
}

export default memo(PageNavigation)
